import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import 'file-saver';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

import { CsvMakeService } from './csv-make.service';
import { reportStyles } from './report-styles';
import { reportHeader } from './report-header';


@Injectable({
  providedIn: 'root',
})
export class ReportHelperService {

  constructor(private csvMake: CsvMakeService, private $date: DatePipe) {}

  /**
   * Build CSV report
   * @param args.report - report data { rows: [], columns: [], styles: [] }
   */
  public async buildCsv(args) {
    const { report } = args;
    const options = { charset: 'utf-8' };

    const rows = report.rows.map(row => row.reduce((res, cell) => { // unwrap colSpans
      res.push(cell);
      return res.concat(new Array((cell && cell.colSpan ? cell.colSpan : 1 || 1) - 1).fill(null));
    }, []));

    // Format data
    rows.forEach(row => {
      row.forEach((cell, idx) => {
        if (row[idx] && row[idx].content !== undefined) row[idx] = row[idx].content;
        const col = report.columns[idx];
        if (col.csv) { // csv formatter
          const converted = col.csv(row[idx]);
          row[idx] = converted === '' ? row[idx] : converted;
        }
        row[idx] = row[idx] === undefined || row[idx] === null || row[idx] === false || row[idx] === '' ? '' : row[idx];
      });
    });

    rows.unshift(report.columns.map(col => col.title.toUpperCase()));

    return new Blob([this.csvMake.generate(rows, options)], { type: 'text/csv;charset=' + options.charset + ';' });
  }


  /**
   * Build jsPdfDoc based on config/params/data/resources
   * @param args.config - merged config + params
   * @param args.config.styles - custom report styles
   * @param args.config.header - custom report header
   * @param args.config.footer - custom report footer
   * @param args.report - report data { rows: [], columns: [], styles: [] }
   * @param args.resources - resources e.g. list of centers
   * @param args.doc - JsPdf document
   * @param args.startY -
   * @returns {*|jsPDF}
   */
  buildPdf(args) {
    const { config, report, resources, startY } = args;
    const doc = new jsPDF('l', 'pt', 'letter');

    // reportFontInit(doc)

    const rows = [...report.rows];

    // Format data
    rows.forEach((row, idx) => {
      rows[idx] = row = [...row];
      let colIdx = 0;
      row.forEach((cell, idx) => {
        if (!cell || cell.content === undefined) row[idx] = cell = { content: cell };
        const col = report.columns[colIdx];
        if (cell.content === false) {
          cell.content = '';
        } else {
          if (col.pdf) { // pdf formatter
            const converted = col.pdf(cell.content);
            cell.content = converted === '' ? cell.content : converted;
          }
          cell.content = cell.content === undefined || cell.content === null || cell.content === '' ? '-' : cell.content;
        }
        colIdx += cell.colSpan || 1;
      });
    });

    const options: Record<string, any> = {
      columns: report.columns.map(col => col.title.toUpperCase()),
      body: rows,
      headStyles: reportStyles.headStyles,
      bodyStyles: reportStyles.bodyStyles,
      // columnStyles: reportStyles.columnStyles,
      tableMargin: reportStyles.pageMargins,
      // startY: startY || reportStyles.startY || false,
      didParseCell: stylesInjector,
      didDrawCell: hd => drawCell(hd, report.columns),
      // TODO: header + footer
      didDrawPage: data => {
        reportHeader({ config, doc, pageNumber: data.pageNumber, $date: this.$date });
      //   (config.footer || reportFooter)({ config, doc, data: data.table, resources });
      },
    };

    options.margin = options.tableMargin || (config.styles || reportStyles).pageMargins;

    autoTable(doc, options);

    return doc;

    function stylesInjector(hd) { // Hook for individual row/cell styling
      const { row, cell, column, section } = hd;
      if (section === 'body') {
        const defaultStyles = { minCellWidth: 40, fillColor: false };
        Object.assign(cell.styles, defaultStyles, report.styles[row.index], report.columns[column.index].styles, rows[row.index][column.index] ? rows[row.index][column.index].styles : null);
      } else {
        Object.assign(cell.styles, report.columns[column.index].styles);
      }
    }

    /**
     * Custom Cell Renderer
     */
    function drawCell(data, columns) {
      const { cell, doc } = data;
      // Border
      if (data.column.index === 0 && cell.styles.borderColor && cell.styles.borderWidth) {
        doc.setLineWidth(cell.styles.borderWidth);
        doc.setDrawColor(cell.styles.borderColor[0], cell.styles.borderColor[1], cell.styles.borderColor[2]);
        doc.rect(
          data.settings.margin.left + cell.styles.borderWidth / 2,
          cell.y + cell.styles.borderWidth / 2,
          data.table.getWidth(doc.internal.pageSize.width) - cell.styles.borderWidth,
          data.row.height - cell.styles.borderWidth * 2,
          'S'
        );
      }

      return false;
    }
  }


  /**
   * Download report file
   * @param blob - blob of pdf or csv
   * @param fileName - file name
   */
  public download({ blob, fileName }) {
    if (!blob) return;
    (window as any).saveAs(blob, fileName);
  }

}
